import { z } from 'zod';
import { Timestamp } from 'firebase/firestore';

/**
 * Fonction utilitaire pour transformer les timestamps Firestore en Date
 */
const transformFirestoreTimestamp = (val: any): Date => {
  // Handle null or undefined
  if (val === null || val === undefined) {
    return new Date(); // Return current date as fallback
  }
  if (val instanceof Timestamp) {
    return val.toDate();
  }
  if (val instanceof Date) {
    return val;
  }
  if (typeof val === 'string') {
    return new Date(val);
  }
  if (val && typeof val === 'object' && val.seconds !== undefined) {
    // Timestamp Firestore sous forme d'objet
    return new Date(val.seconds * 1000);
  }
  throw new Error(`Cannot transform timestamp: ${val}`);
};

/**
 * Schéma Zod pour les timestamps Firestore
 */
const firestoreTimestampSchema = z.any().transform(transformFirestoreTimestamp);

/**
 * Schéma pour un provider OAuth
 */
export const ProviderSchema = z.object({
  type: z.enum(['google', 'facebook', 'apple', 'email']),
  providerUid: z.string(), // ID interne du provider
  email: z.string().email().optional(),
  displayName: z.string().optional(),
  photoURL: z.string().optional(),
});

/**
 * Schéma de validation pour un customer
 */
export const CustomerSchema = z.object({
  id: z.string(),
  firebaseUid: z.string().optional(), // Seulement si utilisateur connecté
  email: z.string().email('Email invalide'),
  providers: z.array(ProviderSchema).default([]),
  isSuperAdmin: z.boolean().optional(), // Super admin plateforme
  // Informations personnelles sauvegardées
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  tiktokUsername: z.string().optional(),
  phone: z.string().optional(),
  address: z.string().optional(),
  houseNumber: z.string().optional(),
  city: z.string().optional(),
  postalCode: z.string().optional(),
  country: z.string().optional(),
  theme: z.string().optional(), // Thème préféré de l'utilisateur
  preferredLanguage: z.string().optional(), // Langue préférée de l'utilisateur (fr, en, es)
  createdAt: firestoreTimestampSchema,
  updatedAt: firestoreTimestampSchema,
});

/**
 * Type Provider pour OAuth
 */
export type Provider = z.infer<typeof ProviderSchema>;

/**
 * Interface TypeScript pour un customer
 */
export interface Customer {
  id: string; // customerId (auto Firestore ID)
  firebaseUid?: string; // Seulement si utilisateur connecté
  email: string;
  providers: Provider[];
  // Super admin plateforme (accès à tout, indépendamment des rôles d'affiliation)
  isSuperAdmin?: boolean;
  // Informations personnelles sauvegardées
  firstName?: string;
  lastName?: string;
  tiktokUsername?: string;
  phone?: string;
  address?: string;
  houseNumber?: string;
  city?: string;
  postalCode?: string;
  country?: string;
  theme?: string; // Thème préféré de l'utilisateur
  preferredLanguage?: string; // Langue préférée de l'utilisateur (fr, en, es)
  createdAt: Date;
  updatedAt: Date;
}

/**
 * Interface pour créer un customer
 */
export interface CreateCustomerRequest {
  firebaseUid?: string;
  email: string;
  providers?: Provider[];
}

/**
 * Interface pour mettre à jour un customer
 */
export interface UpdateCustomerRequest {
  firebaseUid?: string;
  providers?: Provider[];
  // Informations personnelles
  firstName?: string;
  lastName?: string;
  tiktokUsername?: string;
  phone?: string;
  address?: string;
  houseNumber?: string;
  city?: string;
  postalCode?: string;
  country?: string;
  theme?: string; // Thème préféré de l'utilisateur
  preferredLanguage?: string; // Langue préférée de l'utilisateur (fr, en, es)
}


